// Supabase Client Configuration for Direct Storage Access
// This is the SINGLE source of truth for Supabase client - all other modules should import from here
import { createClient, SupabaseClient } from '@supabase/supabase-js';

/**
 * SECURITY: Credential Obfuscation
 *
 * These functions prevent automated scanners from detecting Supabase credentials
 * by avoiding common patterns like:
 * - Direct "eyJ" JWT prefix in source
 * - "*.supabase.co" URL patterns
 * - NEXT_PUBLIC_SUPABASE_* variable names in bundle
 *
 * Note: This is security through obscurity - RLS is your real protection.
 * This just makes it harder for automated tools to find credentials.
 */

// Decode obfuscated credentials at runtime
const deobfuscate = (encoded: string): string => {
  // Simple base64 decode with reversal
  try {
    // Strip quotes if present (some env systems include them)
    const clean = encoded.replace(/^["']|["']$/g, '');
    const reversed = clean.split('').reverse().join('');
    return atob(reversed);
  } catch (_e) {
    return encoded; // Fallback to raw value if not encoded
  }
};

// Check if value looks obfuscated (reversed base64)
const isObfuscated = (value: string): boolean => {
  // Strip quotes if present
  const clean = value.replace(/^["']|["']$/g, '');
  return !clean.startsWith('eyJ') && !clean.startsWith('http');
};

// Get Supabase configuration from environment variables
// These should be set in your .env.local file for development
// and in your Render environment variables for production
const getSupabaseConfig = () => {
  let url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  let anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  // Also check for obfuscated versions (preferred in production)
  const obfuscatedUrl = process.env.NEXT_PUBLIC_SB_U;
  const obfuscatedKey = process.env.NEXT_PUBLIC_SB_K;

  // Use obfuscated values if available
  if (obfuscatedUrl && isObfuscated(obfuscatedUrl)) {
    url = deobfuscate(obfuscatedUrl);
  }
  if (obfuscatedKey && isObfuscated(obfuscatedKey)) {
    anonKey = deobfuscate(obfuscatedKey);
  }

  // Validate that environment variables are set
  if (!url || !anonKey) {
    throw new Error(
      'Database configuration error: Required environment variables must be set'
    );
  }

  return { url, anonKey };
};

// Global singleton using globalThis to ensure only one instance across all modules
// This prevents "Multiple GoTrueClient instances" warning
const GLOBAL_SUPABASE_KEY = '__INSURACENTRAL_SUPABASE_CLIENT__';

declare global {
  // eslint-disable-next-line no-var
  var __INSURACENTRAL_SUPABASE_CLIENT__: SupabaseClient | undefined;
}

// LOGIN-HANG FIX (2026-07-24): Supabase's GoTrue auth calls (signInWithPassword,
// refresh, getUser) use plain fetch with NO timeout. If the auth request stalls —
// connection accepted but no response — the promise NEVER settles, so the Sign In
// button spins forever with no error and NO auth event is ever recorded (matches
// the "press sign in, just stuck loading" reports while the backend is healthy).
// This custom fetch time-boxes ONLY GoTrue auth requests (/auth/v1/), so a stalled
// auth call aborts into a normal error the login form can surface and retry.
// Storage uploads (/storage/v1/, up to 50MB) and realtime are deliberately left
// untouched so a slow large upload is never aborted.
const AUTH_FETCH_TIMEOUT_MS = 15000;
const timeboxAuthFetch: typeof fetch = (input, init) => {
  let url = '';
  try {
    url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : (input as Request).url;
  } catch { /* fall through — treat as non-auth */ }

  if (!url.includes('/auth/v1/')) {
    return fetch(input as any, init);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_FETCH_TIMEOUT_MS);
  // Respect a caller-provided signal too (don't clobber it).
  if (init?.signal) {
    if (init.signal.aborted) controller.abort();
    else init.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return fetch(input as any, { ...init, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

// Synchronous getter for components that need immediate access
export const getSupabaseClientSync = (): SupabaseClient | null => {
  if (globalThis[GLOBAL_SUPABASE_KEY]) {
    return globalThis[GLOBAL_SUPABASE_KEY];
  }

  try {
    const { url, anonKey } = getSupabaseConfig();
    if (!url || !anonKey) {
      return null;
    }

    globalThis[GLOBAL_SUPABASE_KEY] = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
      global: {
        fetch: timeboxAuthFetch,
        headers: {
          'X-Client-Info': 'insuracentral-frontend',
        },
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });

    return globalThis[GLOBAL_SUPABASE_KEY];
  } catch (_e) {
    return null;
  }
};

// Async getter (kept for backwards compatibility)
export const getSupabaseClient = async (): Promise<SupabaseClient> => {
  if (globalThis[GLOBAL_SUPABASE_KEY]) {
    return globalThis[GLOBAL_SUPABASE_KEY];
  }

  try {
    const { url, anonKey } = getSupabaseConfig();

    globalThis[GLOBAL_SUPABASE_KEY] = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
      global: {
        fetch: timeboxAuthFetch,
        headers: {
          'X-Client-Info': 'insuracentral-frontend',
        },
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });

    return globalThis[GLOBAL_SUPABASE_KEY];
  } catch (error) {
    throw error;
  }
};

// Export the client getter
export const supabase = {
  get: getSupabaseClient,
  // Add other methods as needed
};

// Storage bucket names
export const STORAGE_BUCKETS = {
  RESOURCES: 'resources',
  AVATARS: 'avatars',
  DOCUMENTS: 'documents',
  TEMP: 'temp',
  UPLOADS: 'uploads',
} as const;

// File upload configuration
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_MIME_TYPES: [
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    // Audio
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/mp4',
    // Video
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/avi',
    'video/mov',
    // Archives
    'application/zip',
    'application/x-rar-compressed',
    'application/x-7z-compressed',
  ],
  CHUNK_SIZE: 1024 * 1024, // 1MB chunks for large file uploads
} as const;

export default supabase;